import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle2, FileText, Upload } from "lucide-react";
import {
  KYC_DOCUMENT_KINDS,
  KYC_DOCUMENT_KIND_LABELS,
  KYC_STATUS_LABELS,
  PROOF_MAX_BYTES,
  proofMimeSchema,
  requiresKyc,
  type Kyc,
  type KycDocumentKind,
  type KycStatus,
} from "@nh/shared";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Badge, Button, type BadgeVariant } from "@/shared/ui/core";
import { SkeletonCard } from "@/shared/ui/ui";
import { useAuth } from "@/features/auth/auth.context";
import { formatOrderDate } from "@/features/admin/orders/format";
import { fileToBase64, getMyKyc, uploadKycDocument } from "@/features/kyc/kyc.api";
import { CredentialCard } from "@/shared/ui/ui";

const STATUS_VARIANTS: Record<KycStatus, BadgeVariant> = {
  not_started: "neutral",
  pending_review: "warning",
  approved: "success",
  rejected: "error",
};

/**
 * ═══════════════════════════════════════════════════════════════════
 * KYC — ya de verdad (Anahí, 27-ago): tres documentos —identificación
 * por las dos caras y comprobante de domicilio—, cada uno con su botón
 * de subir. El expediente entra EN REVISIÓN solo, con el primer
 * archivo; el veredicto llega del panel de administración, y si es un
 * rechazo, la nota dice QUÉ corregir — y volver a subir reabre la
 * revisión sin pedir nada a nadie.
 *
 * El KYC será requisito para PAGAR comisiones; la tienda funciona
 * desde antes.
 * ═══════════════════════════════════════════════════════════════════
 */
export function KycPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const kyc = useQuery({ queryKey: ["kyc", "me"], queryFn: getMyKyc });
  const [error, setError] = useState<string | null>(null);

  const subir = useMutation({
    mutationFn: uploadKycDocument,
    onSuccess: (fresh) => queryClient.setQueryData(["kyc", "me"], fresh),
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo subir el documento")),
  });

  async function onFile(kind: KycDocumentKind, file: File | undefined) {
    setError(null);
    if (!file) return;

    const mime = proofMimeSchema.safeParse(file.type);
    if (!mime.success) {
      setError(t("Solo se aceptan fotos (JPG, PNG, WebP) o PDF."));
      return;
    }
    if (file.size > PROOF_MAX_BYTES) {
      setError(t("El documento no puede pasar de 4 MB."));
      return;
    }

    const dataBase64 = await fileToBase64(file);
    await subir.mutateAsync({ kind, fileName: file.name, mimeType: mime.data, dataBase64 });
  }

  const expediente = kyc.data;

  const initials = (user?.fullName ?? "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

  // Estados Unidos no sube documentos (Anahí, 22-oct-2026).
  if (!requiresKyc(user?.country)) {
    return (
      <>
        <PageHeader titleHidden title={t("KYC")} />
        <Alert variant="info">
          {t("Tu cuenta es de Estados Unidos: no necesitas subir documentos.")}
        </Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader
        titleHidden
        title={t("KYC")}
        description={t("La verificación de identidad. Será requisito para el pago de comisiones.")}
      />

      {kyc.isPending || !expediente ? (
        <SkeletonCard />
      ) : (
        /*
         * La MISMA CredentialCard de Mi perfil (Anahí, 27-ago: "replica
         * la card... debe ser un componente"): aquí la fila de estado
         * lleva el veredicto del expediente en lugar de "Cuenta activa".
         */
        <CredentialCard
          initials={initials}
          title={user?.fullName ?? ""}
          subtitle={t("Verificación de identidad")}
          statusLeft={
            <Badge variant={STATUS_VARIANTS[expediente.status]} size="sm" dot>
              {t(KYC_STATUS_LABELS[expediente.status])}
            </Badge>
          }
        >
          <div className="grid gap-4">
            {expediente.status === "rejected" && expediente.reviewNote && (
              <Alert variant="error">
                {t("Qué corregir: {{nota}}", { nota: expediente.reviewNote })}
              </Alert>
            )}
            {expediente.status === "pending_review" && (
              <p className="text-[13px] text-ds-ink-3">
                {t("Recibimos tus documentos. Te avisamos al revisarlos.")}
              </p>
            )}

            <div className="grid gap-2.5">
              {KYC_DOCUMENT_KINDS.map((kind) => (
                <DocumentSlot
                  key={kind}
                  kind={kind}
                  kyc={expediente}
                  uploading={subir.isPending && subir.variables?.kind === kind}
                  onFile={(file) => void onFile(kind, file)}
                />
              ))}
            </div>

            {error && (
              <Alert variant="error" onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}
          </div>
        </CredentialCard>
      )}
    </>
  );
}

function DocumentSlot({
  kind,
  kyc,
  uploading,
  onFile,
}: {
  kind: KycDocumentKind;
  kyc: Kyc;
  uploading: boolean;
  onFile: (file: File | undefined) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const uploaded = kyc.documents.find((doc) => doc.kind === kind);

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-ds-border bg-white px-4 py-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-ds-mist text-ds-ink-3">
        {uploaded ? (
          <CheckCircle2 className="h-4 w-4 text-ds-violet-pop" />
        ) : (
          <FileText className="h-4 w-4" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-medium text-ds-ink">
          {t(KYC_DOCUMENT_KIND_LABELS[kind])}
        </p>
        <p className="truncate text-[12px] text-ds-ink-3">
          {uploaded
            ? `${uploaded.fileName} · ${t("subido el {{fecha}}", { fecha: formatOrderDate(uploaded.uploadedAt) })}`
            : t("Foto o PDF, hasta 4 MB.")}
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={(event) => {
          onFile(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
      <Button
        variant={uploaded ? "ghost" : "secondary"}
        size="sm"
        leftIcon={<Upload />}
        loading={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploaded ? t("Reemplazar") : t("Subir")}
      </Button>
    </div>
  );
}
